import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

// Sprint C — UPI top-up sheet (Prompt 7). Every 402 INSUFFICIENT_CREDITS
// anywhere in the app opens the top-up sheet instead of a toast error.
// A listener in <CreditsTopupSheet/> picks up the window event.
api.interceptors.response.use(
  (resp) => resp,
  (err) => {
    try {
      const r = err?.response;
      if (r && r.status === 402) {
        const detail = r.data?.detail || {};
        if (detail?.code === "INSUFFICIENT_CREDITS") {
          window.dispatchEvent(new CustomEvent("kalki:credits-topup-needed", {
            detail: {
              balance: detail.balance ?? 0,
              needed:  detail.needed  ?? 0,
              message: detail.message || "You need more Credits to continue.",
            },
          }));
        }
      }
    } catch (_) {}
    return Promise.reject(err);
  },
);

export function useAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const checkedRef = useRef(false);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user || null);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (checkedRef.current) return;
    checkedRef.current = true;
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = useCallback((next) => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Stash the intended post-login destination so AuthCallback can
    // bounce the user back to the page they came from (e.g. /inner-mirror).
    try {
      const intent = typeof next === "string" && next.startsWith("/")
        ? next
        : (window.location.pathname + window.location.search);
      if (intent && intent !== "/" && intent !== "/app/chat") {
        sessionStorage.setItem("kalki_post_login_next", intent);
      }
    } catch (_) {}
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (e) {}
    setUser(null);
  }, []);

  return { user, loading, login, logout, refresh: checkAuth, setUser };
}
